import Label from '@/components/core/label';
import { ChoicesOrderItemType } from '@/sections/administrative/accounts/orders/context';
import { OrderDocumentsSection } from '@/sections/administrative/accounts/orders/forms/components/order-documents-section';
import { ChoicesOrderItemLabel } from '@/types/order';
import { IOrderDocument } from '@/types/order-document';
import { RHFNumberText } from '@components/forms/hook-form/rhf-number-field';
import Iconify from '@components/media/iconify';
import { CopyText } from '@components/utils/copy-text';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import { useMemo, useState } from 'react';

// Minimal item type required for distribution tabs
interface MinimalDistributionItem {
  subscription: string;
  type: string;
  productName: string;
  plan: { name: string; code: string };
  partnerLogo: string;
  sku: string;
  quantity: number;
  totalAmount: number;
}

// Minimal distribution list item type
interface MinimalDistributionListItem {
  legalEntity: { _id: string; name: string; taxNumber: string };
  items: MinimalDistributionItem[];
  documents?: IOrderDocument[];
}

interface BillingDistributionTabsProps {
  distributionListItem: MinimalDistributionListItem[];
}

export const BillingDistributionTabs = ({
  distributionListItem: distribuitionListItem,
}: BillingDistributionTabsProps) => {
  const [tabValue, setTabValue] = useState<string>('0');
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [selectedLegalEntityDocuments, setSelectedLegalEntityDocuments] = useState<
    IOrderDocument[]
  >([]);
  const [selectedLegalEntityName, setSelectedLegalEntityName] = useState('');
  const theme = useTheme();

  const totalAmount = useMemo(() => {
    let total = 0;
    distribuitionListItem.forEach((distribution) => {
      distribution.items.forEach((item) => {
        total += item.totalAmount;
      });
    });
    return total;
  }, [distribuitionListItem]);

  const handleViewDocuments = (distribution: MinimalDistributionListItem) => {
    const legalEntityName = distribution.legalEntity.name || '';
    const documents = distribution.documents || [];

    setSelectedLegalEntityName(legalEntityName);
    setSelectedLegalEntityDocuments(documents);
    setShowDocumentsDialog(true);
  };

  const handleCloseDocumentsDialog = () => {
    setShowDocumentsDialog(false);
    setSelectedLegalEntityDocuments([]);
    setSelectedLegalEntityName('');
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title={t('Billing distribution')} />
      <CardContent sx={{ px: 0, pb: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ width: '100%' }}>
          <TabContext value={tabValue}>
            <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
              {/* Tabs List */}
              <TabList onChange={(event, newValue) => setTabValue(newValue)}>
                {distribuitionListItem.map((distribution, distributionIndex) => (
                  <Tab
                    key={`tab-${distribution.legalEntity.name}`}
                    label={distribution.legalEntity.name}
                    value={distributionIndex.toString()}
                    wrapped
                  />
                ))}
              </TabList>
            </Box>
            {/* Tab Content */}
            {distribuitionListItem.map((distribution, distributionIndex) => (
              <TabPanel
                key={`tab-${distribution.legalEntity.name}`}
                value={distributionIndex.toString()}
              >
                <CardHeader
                  title={
                    <>
                      <Iconify
                        icon="solar:bill-bold-duotone"
                        width={20}
                        sx={{ verticalAlign: 'middle' }}
                      />{' '}
                      {distribution.legalEntity.name}
                    </>
                  }
                />

                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Stack spacing={4} direction="row">
                    <Typography fontWeight="bold">{`${t('Tax number')} :`}</Typography>
                    <Typography>{distribution.legalEntity.taxNumber}</Typography>
                  </Stack>
                  {distribution.documents && distribution.documents.length > 0 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight="bold">{t('Attached Documents')}</Typography>

                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<AttachFileIcon />}
                        onClick={() => handleViewDocuments(distribution)}
                        endIcon={
                          <Box
                            sx={{
                              backgroundColor:
                                theme.palette.mode === 'dark' ? 'lightgrey' : 'darkgray',
                              color: 'black',
                              borderRadius: '50%',
                              minWidth: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                              }}
                            >
                              {distribution.documents.length}
                            </Typography>
                          </Box>
                        }
                      >
                        {t('View Documents')}
                      </Button>
                    </Stack>
                  )}
                </CardContent>
                <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('Product')}</TableCell>
                        <TableCell>{t('Catalog name')}</TableCell>
                        <TableCell>{t('Type')}</TableCell>
                        <TableCell align="center">{t('Unit Amount')}</TableCell>
                        <TableCell>{t('Total Amount')}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {distribution.items.map((distributionItem, distributionItemIndex) => (
                        <TableRow
                          key={`${distributionIndex}-distribution-item-${distributionItemIndex}`}
                        >
                          <TableCell>
                            <Typography variant="subtitle2">
                              {`${distributionItem.productName} / ${distributionItem.sku}`}
                            </Typography>
                            <Chip
                              avatar={
                                <Avatar
                                  alt={distributionItem.productName}
                                  src={decodeURI(distributionItem.partnerLogo)}
                                />
                              }
                              variant="outlined"
                              size="small"
                              label={distributionItem.productName}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack>
                              <Typography variant="caption">
                                {distributionItem.plan.name}
                              </Typography>
                              <CopyText
                                text={distributionItem.plan.code}
                                color="text.secondary"
                                variant="caption"
                                sx={{ fontSize: 'inherit' }}
                              />
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Label
                              color={
                                distributionItem.type === ChoicesOrderItemType.newResource
                                  ? 'primary'
                                  : 'secondary'
                              }
                              variant="outlined"
                            >
                              {ChoicesOrderItemLabel[distributionItem.type]}
                            </Label>
                          </TableCell>
                          <TableCell align="center">
                            <Label>{distributionItem.quantity}</Label>
                          </TableCell>

                          <TableCell align="center">
                            <RHFNumberText
                              value={distributionItem.totalAmount}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix="$ "
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
                <Divider />

                <CardActions sx={{ px: 3, pt: 2, pb: 0 }}>
                  <Grid container spacing={2} flexGrow={1}>
                    <Grid item xs={7} md={9} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle1">{t('Total')}:</Typography>
                    </Grid>
                    <Grid item xs={5} md={3} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle1" color="primary.main">
                        <RHFNumberText
                          value={totalAmount}
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$ "
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                </CardActions>
              </TabPanel>
            ))}
          </TabContext>
        </Box>
      </CardContent>

      {/* Documents Dialog */}
      <Dialog
        open={showDocumentsDialog}
        onClose={handleCloseDocumentsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('Attached Documents')}</Typography>
            <IconButton onClick={handleCloseDocumentsDialog}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('View documents related to this purchase order.')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <OrderDocumentsSection
            documents={selectedLegalEntityDocuments}
            title={selectedLegalEntityName}
            sectionsToHide={{ AddButton: true, EditButton: true, DeleteButton: true }}
            NormalView={true}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};
